import { useMemo, useState, useEffect, useRef } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { 
  ExternalLink, 
  Copy, 
  Check, 
  Clock, 
  ChevronRight,
  ChevronLeft,
  Palette,
  FileText,
  Smartphone,
  User,
  Wifi,
  Battery,
  Signal,
  CheckCircle2,
  CalendarDays,
  QrCode,
  Code,
  Share2,
  Twitter,
  Facebook,
  MessageCircle,
  Download
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "@/hooks/use-business";
import { useServices } from "@/hooks/use-services";
import { useStaff } from "@/hooks/use-staff";
import { useServiceCategories } from "@/hooks/use-service-categories";
import { cn } from "@/lib/utils";
import { themeVarsFromHex } from "@/lib/theme";
import { format, addDays, startOfDay } from "date-fns";
import { LogoStack } from "@/components/LogoStack";
import { getBaseUrl } from "@/lib/site";

type MediaItem = { url: string; type: "image" | "video" };

const normalizeMediaItems = (raw: any, cap = 6): MediaItem[] => {
  if (!Array.isArray(raw) || cap <= 0) return [];
  const out: MediaItem[] = [];
  const seen = new Set<string>();
  for (const it of raw) {
    const url = String(it?.url || "").trim();
    if (!url) continue;
    const tRaw = String(it?.type || "").toLowerCase();
    const type: MediaItem["type"] =
      tRaw === "video" || tRaw === "image"
        ? (tRaw as any)
        : (() => {
            const lower = url.toLowerCase();
            return lower.endsWith(".mp4") ||
              lower.endsWith(".webm") ||
              lower.endsWith(".mov") ||
              lower.endsWith(".avi") ||
              lower.endsWith(".mkv")
              ? "video"
              : "image";
          })();
    const key = `${type}:${url}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ url, type });
    if (out.length >= cap) break;
  }
  return out;
};

// Helper function to get currency symbol
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    GHS: "₵",
    NGN: "₦",
    USD: "$",
    EUR: "€",
    GBP: "£",
    ZAR: "R",
    KES: "KSh",
  };
  return symbols[currency] || currency;
};

const timeSlots = ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "14:00", "14:30", "15:00"];

type PreviewStep = "service" | "staff" | "datetime" | "details" | "confirm" | "success";

export default function BookingPagePreview() {
  const { toast } = useToast();
  const { business, refetch } = useBusiness();
  const { services } = useServices();
  const { staff: staffMembers } = useStaff();
  const { categories: serviceCategories } = useServiceCategories();
  const [copied, setCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [settings, setSettings] = useState({
    primaryColor: "#2563EB",
    logoUrl: "",
    welcomeMessage: "Book your appointment online, anytime",
    cancellationPolicy: "Please cancel at least 24 hours in advance",
    termsAndConditions: "",
  });

  const previewThemeStyle = useMemo(() => themeVarsFromHex(settings.primaryColor), [settings.primaryColor]);

  // Update settings when business data changes
  useEffect(() => {
    if (business) {
      setSettings(prev => ({
        ...prev,
        primaryColor: (business as any)?.primary_color || prev.primaryColor,
        logoUrl: (business as any)?.logo_url || prev.logoUrl,
        welcomeMessage: business?.description || prev.welcomeMessage,
        cancellationPolicy: (business as any)?.cancellation_policy || prev.cancellationPolicy,
        termsAndConditions: (business as any)?.terms_and_conditions || prev.termsAndConditions,
      }));
    }
  }, [business]);

  useEffect(() => {
    return () => {
      if (settings.logoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(settings.logoUrl);
      }
    };
  }, [settings.logoUrl]);

  const base = getBaseUrl();
  const bookingUrl = business?.slug ? `${base}/${business.slug}` : `${base}/demo-business`;
  
  const currencySymbol = getCurrencySymbol(business?.currency || "USD");
  
  // Use real services or demo data
  const allDisplayServices = services && services.length > 0 ? services.map(s => ({
    id: s.id,
    name: s.name,
    duration: s.duration_minutes,
    price: s.price_amount,
    description: s.description || "",
    image_url: (s as any).image_url || undefined,
    category_id: (s as any).category_id || null
  })) : [
    { id: "1", name: "Haircut", duration: 30, price: 25, description: "Professional haircut service", image_url: undefined, category_id: null },
    { id: "2", name: "Beard Trim", duration: 15, price: 15, description: "Clean beard trim and shape", image_url: undefined, category_id: null },
    { id: "3", name: "Swedish Massage", duration: 60, price: 80, description: "Relaxing full body massage", image_url: undefined, category_id: null },
  ];
  
  // Use real staff or demo data
  const displayStaff = [
    { id: "any", name: "Any Available" },
    ...((staffMembers && staffMembers.length > 0) ? staffMembers.map(s => ({
      id: s.id,
      name: s.name
    })) : [
      { id: "1", name: "Alex Johnson" },
      { id: "2", name: "Ama Mensah" },
    ])
  ];

  const handleCopyLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    setCopied(true);
    toast({ title: "Link copied to clipboard" });
    setTimeout(() => setCopied(false), 2000);
  };

  const copyText = async (text: string, successTitle = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: successTitle });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const shareMessage = business?.name
    ? `Book with ${business.name}: ${bookingUrl}`
    : `Book here: ${bookingUrl}`;

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(
    bookingUrl,
  )}`;

  const downloadQr = async () => {
    try {
      const res = await fetch(qrUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${business?.slug || "booking"}-qr.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast({ title: "Download failed", description: e?.message || "Please try again.", variant: "destructive" });
    }
  };

  const openShare = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement> | { target: { files: FileList } }) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file",
        description: "Please choose an image file for your logo.",
        variant: "destructive",
      });
      return;
    }

    const nextPreview = URL.createObjectURL(file);
    setSettings((prev) => {
      if (prev.logoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(prev.logoUrl);
      }
      return { ...prev, logoUrl: nextPreview };
    });
    setLogoFile(file);
    setLogoRemoved(false);
  };

  const handleSaveChanges = async () => {
    if (!business) {
      toast({ title: "No business found", variant: "destructive" });
      return;
    }

    // Validate primary color
    if (!/^#[0-9A-Fa-f]{6}$/.test(settings.primaryColor.trim())) {
      toast({
        title: "Invalid color",
        description: "Please enter a valid hex color (e.g., #2563EB)",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      let logoUrl: string | null = null;

      if (logoRemoved) {
        logoUrl = null;

        if ((business as any)?.logo_url) {
          const oldFileName = String((business as any).logo_url).split("/").slice(-2).join("/");
          try {
            await supabase.storage.from("service-media").remove([oldFileName]);
          } catch (err) {
            console.log("Could not delete old logo:", err);
          }
        }
      } else if (logoFile) {
        const fileExt = logoFile.name.split(".").pop();
        const fileName = `logos/${business.id}/${Date.now()}.${fileExt}`;

        if ((business as any)?.logo_url) {
          const oldFileName = String((business as any).logo_url).split("/").slice(-2).join("/");
          try {
            await supabase.storage.from("service-media").remove([oldFileName]);
          } catch (err) {
            console.log("Could not delete old logo:", err);
          }
        }

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("service-media")
          .upload(fileName, logoFile, {
            cacheControl: "3600",
            upsert: false,
            contentType: logoFile.type || undefined,
          });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("service-media")
          .getPublicUrl(uploadData.path);
        logoUrl = urlData.publicUrl;
      } else {
        logoUrl =
          settings.logoUrl && !settings.logoUrl.startsWith("blob:")
            ? settings.logoUrl.trim()
            : ((business as any)?.logo_url ?? null);
      }

      const updateData: any = {
        primary_color: settings.primaryColor.trim(),
        logo_url: logoUrl,
        description: settings.welcomeMessage.trim() || null,
        cancellation_policy: settings.cancellationPolicy.trim() || null,
        terms_and_conditions: settings.termsAndConditions.trim() || null,
      };

      const { error } = await supabase
        .from("businesses")
        .update(updateData)
        .eq("id", business.id);

      if (error) throw error;

      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
      setLogoFile(null);
      setLogoRemoved(false);
      if (logoUrl !== null) {
        setSettings((prev) => ({ ...prev, logoUrl: logoUrl || "" }));
      } else {
        setSettings((prev) => ({ ...prev, logoUrl: "" }));
      }
      await refetch();
      toast({ title: "Saved", description: "Booking page settings updated successfully." });
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message || "Please try again.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  // Interactive iPhone Preview Component
  const IPhonePreview = ({
    className = "",
    themeStyle,
  }: {
    className?: string;
    themeStyle?: React.CSSProperties;
  }) => {
    const [step, setStep] = useState<PreviewStep>("service");
    const [selectedServiceCategory, setSelectedServiceCategory] = useState<"all" | "uncategorized" | string>("all");
    const [selectedService, setSelectedService] = useState<typeof allDisplayServices[0] | null>(null);
    const [selectedStaff, setSelectedStaff] = useState<typeof displayStaff[0] | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);

    // Filter services by category
    const categoryFilteredServices =
      selectedServiceCategory === "all"
        ? allDisplayServices
        : selectedServiceCategory === "uncategorized"
          ? allDisplayServices.filter((s) => !s.category_id)
          : allDisplayServices.filter((s) => String(s.category_id) === String(selectedServiceCategory));
    
    const displayServices = categoryFilteredServices;

    const today = startOfDay(new Date());
    const steps: PreviewStep[] = ["service", "staff", "datetime", "details", "confirm"];
    const currentStepIndex = steps.indexOf(step);

    const handleServiceSelect = (service: typeof displayServices[0]) => {
      setSelectedService(service);
      setStep("staff");
    };

    const handleStaffSelect = (staff: typeof displayStaff[0]) => {
      setSelectedStaff(staff);
      setStep("datetime");
    };

    const handleTimeSelect = (time: string) => {
      setSelectedTime(time);
    };

    const goBack = () => {
      if (step === "success") {
        resetPreview();
        return;
      }
      const prevIndex = currentStepIndex - 1;
      if (prevIndex >= 0) {
        setStep(steps[prevIndex]);
      }
    };

    const goNext = () => {
      const nextIndex = currentStepIndex + 1;
      if (nextIndex < steps.length) {
        setStep(steps[nextIndex]);
      }
    };

    const handleConfirm = () => {
      setStep("success");
    };

    const resetPreview = () => {
      setStep("service");
      setSelectedService(null);
      setSelectedStaff(null);
      setSelectedDate(undefined);
      setSelectedTime(null);
    };

    const previewLogoUrl = String(
      (settings.logoUrl || (business as any)?.logo_url || ""),
    ).trim();
    const previewHasLogo = !!previewLogoUrl;

    const previewHeaderMedia = (business as any)?.booking_header_media_items;
    const heroItems = useMemo(() => {
      const fromHeader = normalizeMediaItems(previewHeaderMedia, 6);
      if (fromHeader.length > 0) return fromHeader;

      // Fallback to service images in preview (keeps preview useful even without header media configured)
      const svc = (allDisplayServices || [])
        .map((s: any) => ({ url: String(s?.image_url || "").trim(), type: "image" as const }))
        .filter((x) => !!x.url);
      return normalizeMediaItems(svc, 6);
    }, [business?.id, previewHeaderMedia, allDisplayServices]);

    const [heroIndex, setHeroIndex] = useState(0);
    useEffect(() => {
      setHeroIndex(0);
    }, [business?.id]);

    const heroCurrent = heroItems[heroIndex] || null;
    const heroHasMultiple = heroItems.length > 1;

    useEffect(() => {
      if (step !== "service") return;
      if (!heroHasMultiple) return;
      if (heroCurrent?.type === "video") return;
      const id = window.setInterval(() => {
        setHeroIndex((prev) => (prev + 1) % heroItems.length);
      }, 5000);
      return () => window.clearInterval(id);
    }, [step, heroHasMultiple, heroCurrent?.type, heroItems.length]);

    return (
      <div className={`flex justify-center ${className}`}>
        {/* iPhone Frame */}
        <div className="relative">
          {/* Phone outer shell - titanium color */}
          <div className="bg-gradient-to-b from-[#3a3a3c] to-[#2c2c2e] rounded-[3rem] p-[3px] shadow-2xl">
            {/* Phone inner bezel */}
            <div className="bg-background rounded-[2.8rem] overflow-hidden w-[280px] sm:w-[300px] relative">
              {/* Status Bar */}
              <div className="bg-background px-6 pt-3 pb-1 flex items-center justify-between relative z-20">
                <span className="text-xs font-medium text-foreground">9:41</span>
                {/* Dynamic Island */}
                <div className="absolute left-1/2 -translate-x-1/2 top-2">
                  <div className="w-[90px] h-[28px] bg-black rounded-full flex items-center justify-center gap-2">
                    <div className="w-[10px] h-[10px] rounded-full bg-[#1a1a1a] border border-[#2a2a2a]" />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Signal className="h-3.5 w-3.5 text-foreground" />
                  <Wifi className="h-3.5 w-3.5 text-foreground" />
                  <Battery className="h-4 w-4 text-foreground" />
                </div>
              </div>
              
              {/* Screen Content */}
              <div className="h-[520px] sm:h-[560px] overflow-y-auto bg-background" style={themeStyle}>
                {/* Header */}
                {step !== "success" && (
                  <div className="sticky top-0 bg-card/95 backdrop-blur-sm border-b border-border px-4 py-3 z-10 flex items-center gap-3">
                    {step !== "service" && (
                      <button onClick={goBack} className="text-primary" aria-label="Go back">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                    )}
                    <h1 className="font-semibold text-foreground text-sm flex-1">
                      {step === "service" && "Book an Appointment"}
                      {step === "staff" && "Select Staff"}
                      {step === "datetime" && "Select Date & Time"}
                      {step === "details" && "Your Details"}
                      {step === "confirm" && "Confirm Booking"}
                    </h1>
                  </div>
                )}
                
                {/* Page Content */}
                <div className="p-4">
                  {/* Business Header - only on service step */}
                  {step === "service" && (
                    <div className="-mx-4 -mt-4 mb-5">
                      <div className="relative bg-muted h-40 overflow-hidden">
                        {heroCurrent ? (
                          heroCurrent.type === "video" ? (
                            <video
                              src={heroCurrent.url}
                              className="absolute inset-0 h-full w-full object-cover object-center"
                              muted
                              playsInline
                              autoPlay
                              preload="metadata"
                              loop={!heroHasMultiple}
                              onEnded={() => {
                                if (!heroHasMultiple) return;
                                setHeroIndex((prev) => (prev + 1) % heroItems.length);
                              }}
                              onError={() => {
                                if (!heroHasMultiple) return;
                                setHeroIndex((prev) => (prev + 1) % heroItems.length);
                              }}
                            />
                          ) : (
                            <img
                              src={heroCurrent.url}
                              alt=""
                              aria-hidden="true"
                              className="absolute inset-0 h-full w-full object-cover object-center"
                            />
                          )
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background" />
                        )}

                        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />

                        <div className="absolute inset-0 z-20 px-4 pb-4 flex flex-col justify-end">
                          <div className="inline-flex max-w-full items-center gap-2 rounded-2xl bg-black/35 px-3 py-2 backdrop-blur-sm">
                            {previewHasLogo ? (
                              <div className="shrink-0">
                                <LogoStack
                                  primaryLogoUrl={previewLogoUrl}
                                  primaryLabel={`${business?.name || "Business"} logo`}
                                  fallbackText={(business?.name || "B").substring(0, 2).toUpperCase()}
                                  logos={[]}
                                  size={34}
                                  collapsedStep={18}
                                  expandedGap={10}
                                />
                              </div>
                            ) : null}
                            <div className="min-w-0">
                              <h2 className="truncate text-lg font-bold leading-tight text-white drop-shadow-sm">
                                {business?.name || "Demo Business"}
                              </h2>
                              <p className="mt-0.5 line-clamp-2 text-xs text-white/90">
                                {business?.description || settings.welcomeMessage}
                              </p>
                            </div>
                          </div>
                        </div>

                        {heroHasMultiple ? (
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
                            {heroItems.map((_, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setHeroIndex(i)}
                                className={cn(
                                  "h-1 rounded-full transition-all",
                                  i === heroIndex ? "w-4 bg-white/90" : "w-2 bg-white/45",
                                )}
                                aria-label={`Go to header media ${i + 1}`}
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {/* Progress Steps - hide on success */}
                  {step !== "success" && (
                    <div className="flex items-center justify-center gap-1.5 mb-5">
                      {steps.map((s, i) => (
                        <div key={s} className="flex items-center">
                          <div 
                            className={cn(
                              "h-2 w-2 rounded-full transition-colors",
                              i <= currentStepIndex ? "bg-primary" : "bg-muted"
                            )} 
                          />
                          {i < steps.length - 1 && (
                            <div className={cn(
                              "w-6 h-0.5 mx-0.5 transition-colors",
                              i < currentStepIndex ? "bg-primary" : "bg-muted"
                            )} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Step: Service Selection */}
                  {step === "service" && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-foreground mb-3">Select a Service</h3>
                      
                      {/* Category Filters */}
                      {(serviceCategories.length > 0 || allDisplayServices.some((s) => !s.category_id)) && (
                        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 mb-3">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedServiceCategory("all");
                              if (selectedService) setSelectedService(null);
                            }}
                            className={cn(
                              "whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                              selectedServiceCategory === "all"
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background border-border hover:border-primary/50",
                            )}
                          >
                            All
                          </button>
                          {serviceCategories
                            .filter(c => {
                              // Only show active categories
                              if ((c as any).active === false) return false;
                              // Only show categories that have at least one service
                              return allDisplayServices.some((s) => String(s.category_id) === String(c.id));
                            })
                            .map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                setSelectedServiceCategory(c.id);
                                if (selectedService && String((selectedService as any).category_id) !== String(c.id)) {
                                  setSelectedService(null);
                                }
                              }}
                              className={cn(
                                "whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                                selectedServiceCategory === c.id
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : "bg-background border-border hover:border-primary/50",
                              )}
                            >
                              {c.name}
                            </button>
                          ))}
                          {allDisplayServices.some((s) => !s.category_id) && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedServiceCategory("uncategorized");
                                if (selectedService && (selectedService as any).category_id) {
                                  setSelectedService(null);
                                }
                              }}
                              className={cn(
                                "whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                                selectedServiceCategory === "uncategorized"
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : "bg-background border-border hover:border-primary/50",
                              )}
                            >
                              Other
                            </button>
                          )}
                        </div>
                      )}

                      {displayServices.length === 0 ? (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                          No services in this category.
                        </div>
                      ) : (
                        displayServices.map((service) => (
                          <button
                            key={service.id}
                            onClick={() => handleServiceSelect(service)}
                            className="w-full flex items-center gap-2 p-3 rounded-xl border border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-left overflow-hidden"
                          >
                            {service.image_url && (
                              <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-muted">
                                <img 
                                  src={service.image_url} 
                                  alt={service.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-foreground text-sm">{service.name}</h4>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{service.description}</p>
                              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                <span className="flex items-center gap-0.5">
                                  <Clock className="h-3 w-3" />
                                  {service.duration} min
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <span className="font-semibold text-foreground text-sm">{currencySymbol}{service.price}</span>
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  {/* Step: Staff Selection */}
                  {step === "staff" && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-foreground mb-3">Choose a Staff Member</h3>
                      {displayStaff.map((staff) => (
                        <button
                          key={staff.id}
                          onClick={() => handleStaffSelect(staff)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-left"
                        >
                          <div className="h-10 w-10 rounded-full flex items-center justify-center bg-primary text-primary-foreground font-medium text-sm">
                            {staff.id === "any" ? <User className="h-4 w-4" /> : staff.name.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-foreground text-sm">{staff.name}</h4>
                            {staff.id === "any" && (
                              <p className="text-xs text-muted-foreground">First available</p>
                            )}
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Step: Date & Time */}
                  {step === "datetime" && (
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <CalendarDays className="h-4 w-4 text-primary" />
                          <h3 className="text-sm font-semibold text-foreground">Select a Date</h3>
                        </div>
                        <div className="rounded-lg border border-border bg-card overflow-hidden">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={setSelectedDate}
                            disabled={(date) => date < today}
                            className="w-full p-2 pointer-events-auto"
                            classNames={{
                              months: "flex w-full",
                              month: "w-full space-y-3",
                              caption: "flex justify-center pt-1 relative items-center w-full",
                              caption_label: "text-sm font-semibold",
                              nav: "space-x-1 flex items-center",
                              nav_button: "h-7 w-7 rounded-full border border-border bg-background p-0 opacity-70 hover:opacity-100",
                              nav_button_previous: "absolute left-0",
                              nav_button_next: "absolute right-0",
                              table: "w-full border-collapse",
                              head_row: "grid grid-cols-7 w-full",
                              head_cell: "text-muted-foreground text-center text-[11px] font-medium",
                              row: "grid grid-cols-7 w-full mt-2",
                              cell: "relative flex h-9 items-center justify-center p-0 text-center text-xs",
                              day: "h-8 w-8 rounded-full p-0 text-xs font-normal hover:bg-primary/10 aria-selected:bg-primary aria-selected:text-primary-foreground",
                              day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                              day_today: "bg-primary/10 text-primary",
                              day_outside: "text-muted-foreground opacity-40",
                              day_disabled: "text-muted-foreground opacity-40",
                              day_hidden: "invisible",
                            }}
                          />
                        </div>
                      </div>
                      {selectedDate && (
                        <div>
                          <h3 className="text-sm font-semibold text-foreground mb-2">
                            Times for {format(selectedDate, "EEE, MMM d")}
                          </h3>
                          <div className="grid grid-cols-3 gap-2">
                            {timeSlots.map((time) => (
                              <button
                                key={time}
                                onClick={() => handleTimeSelect(time)}
                                className={cn(
                                  "py-2 px-2 rounded-lg text-xs font-medium transition-colors",
                                  selectedTime === time
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted hover:bg-primary/5 text-foreground"
                                )}
                              >
                                {time}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedDate && selectedTime && (
                        <Button 
                          size="sm" 
                          className="w-full mt-2"
                          onClick={goNext}
                        >
                          Continue
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Step: Details */}
                  {step === "details" && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-foreground mb-3">Your Details</h3>
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Full Name</Label>
                          <Input placeholder="Your name" className="h-9 text-sm" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Email</Label>
                          <Input type="email" placeholder="you@email.com" className="h-9 text-sm" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Phone</Label>
                          <Input type="tel" placeholder="+233 XXX XXX XXX" className="h-9 text-sm" />
                        </div>
                      </div>
                      <Button size="sm" className="w-full mt-4" onClick={goNext}>
                        Review Booking
                      </Button>
                    </div>
                  )}

                  {/* Step: Confirm */}
                  {step === "confirm" && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-foreground mb-3">Booking Summary</h3>
                      <div className="bg-accent/50 rounded-xl p-4 space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Service</span>
                          <span className="font-medium text-foreground">{selectedService?.name}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Staff</span>
                          <span className="font-medium text-foreground">{selectedStaff?.name}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Date & Time</span>
                          <span className="font-medium text-foreground">
                            {selectedDate ? format(selectedDate, "EEE, MMM d") : ""} at {selectedTime}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t border-border">
                          <span className="text-muted-foreground">Total</span>
                          <span className="font-bold text-foreground">{currencySymbol}{selectedService?.price}</span>
                        </div>
                      </div>
                      <Button size="sm" className="w-full" onClick={handleConfirm}>
                        Confirm Booking
                      </Button>
                      <p className="text-[10px] text-muted-foreground text-center">
                        {settings.cancellationPolicy}
                      </p>
                    </div>
                  )}

                  {/* Step: Success */}
                  {step === "success" && (
                    <div className="text-center py-6">
                      <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                      </div>
                      <h2 className="text-lg font-bold text-foreground mb-2">Booking Confirmed!</h2>
                      <p className="text-xs text-muted-foreground mb-6">
                        We've sent a confirmation to your email.
                      </p>

                      <div className="bg-accent/50 rounded-xl p-4 text-left space-y-2 mb-6">
                        <h3 className="font-semibold text-foreground text-sm mb-3">Appointment Details</h3>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Service</span>
                          <span className="font-medium text-foreground">{selectedService?.name}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Staff</span>
                          <span className="font-medium text-foreground">{selectedStaff?.name}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Date & Time</span>
                          <span className="font-medium text-foreground">
                            {selectedDate ? format(selectedDate, "EEE, MMM d") : ""} at {selectedTime}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs pt-2 border-t border-border">
                          <span className="text-muted-foreground">Total</span>
                          <span className="font-bold text-foreground">{currencySymbol}{selectedService?.price}</span>
                        </div>
                      </div>

                      <Button size="sm" variant="outline" className="w-full" onClick={resetPreview}>
                        Book Another Appointment
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Home Indicator */}
              <div className="bg-background h-8 flex items-center justify-center">
                <div className="w-28 h-1 bg-foreground/20 rounded-full" />
              </div>
            </div>
          </div>
          
          {/* Side buttons */}
          <div className="absolute left-0 top-28 w-[3px] h-8 bg-[#3a3a3c] rounded-l-sm" />
          <div className="absolute left-0 top-40 w-[3px] h-12 bg-[#3a3a3c] rounded-l-sm" />
          <div className="absolute left-0 top-56 w-[3px] h-12 bg-[#3a3a3c] rounded-l-sm" />
          <div className="absolute right-0 top-36 w-[3px] h-16 bg-[#3a3a3c] rounded-r-sm" />
        </div>
      </div>
    );
  };

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title="Booking Page"
        description="Preview and customize how customers see your booking page"
      />

      <Tabs defaultValue="preview" className="space-y-4 sm:space-y-6">
        <TabsList className="glass-card w-full sm:w-auto overflow-x-auto">
          <TabsTrigger value="preview" className="flex-1 sm:flex-none">
            <Smartphone className="h-4 w-4 mr-2 hidden sm:inline" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="customize" className="flex-1 sm:flex-none">
            <Palette className="h-4 w-4 mr-2 hidden sm:inline" />
            Customize
          </TabsTrigger>
          <TabsTrigger value="share" className="flex-1 sm:flex-none">
            <ExternalLink className="h-4 w-4 mr-2 hidden sm:inline" />
            Share
          </TabsTrigger>
        </TabsList>

        {/* Preview Tab */}
        <TabsContent value="preview" className="space-y-4 sm:space-y-6">
          {/* Booking Link Bar */}
          <div className="glass-card p-3 sm:p-4 space-y-3 sm:space-y-0 sm:flex sm:items-center sm:gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground mb-1">Your booking page</p>
              <code className="text-xs sm:text-sm text-foreground bg-accent px-2 py-1 rounded block truncate">
                {bookingUrl}
              </code>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCopyLink} className="flex-1 sm:flex-none">
                {copied ? <Check className="h-4 w-4 mr-1 sm:mr-2" /> : <Copy className="h-4 w-4 mr-1 sm:mr-2" />}
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button size="sm" asChild className="flex-1 sm:flex-none">
                <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-1 sm:mr-2" />
                  Open
                </a>
              </Button>
            </div>
          </div>

          {/* iPhone Preview */}
          <div className="glass-card p-4 sm:p-8">
            <div className="mb-4 flex items-center justify-center gap-2">
              <span className="text-xs text-muted-foreground">Interactive Preview • Tap to navigate</span>
            </div>
            <IPhonePreview themeStyle={previewThemeStyle} />
          </div>
        </TabsContent>

        {/* Customize Tab */}
        <TabsContent value="customize" className="space-y-4 sm:space-y-6">
          <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Settings Form */}
            <div className="space-y-4 sm:space-y-6">
              {/* Branding */}
              <div className="glass-card p-4 sm:p-6 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Palette className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground">Branding</h3>
                </div>
                
                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex gap-2 sm:gap-3">
                    <Input
                      type="color"
                      value={settings.primaryColor}
                      onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.primaryColor}
                      onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Logo</Label>
                  <div
                    className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => logoInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const files = e.dataTransfer.files;
                      if (files.length > 0) {
                        handleLogoSelect({ target: { files } } as any);
                      }
                    }}
                  >
                    {settings.logoUrl ? (
                      <div className="space-y-4">
                        <img
                          src={settings.logoUrl}
                          alt="Logo preview"
                          className="max-h-24 mx-auto rounded-lg object-contain"
                        />
                        <div className="flex gap-2 justify-center">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              logoInputRef.current?.click();
                            }}
                          >
                            Change Logo
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSettings((prev) => {
                                if (prev.logoUrl.startsWith("blob:")) {
                                  URL.revokeObjectURL(prev.logoUrl);
                                }
                                return { ...prev, logoUrl: "" };
                              });
                              setLogoFile(null);
                              setLogoRemoved(true);
                              if (logoInputRef.current) {
                                logoInputRef.current.value = "";
                              }
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Drag and drop your logo here, or click to browse
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          className="mt-4"
                          onClick={(e) => {
                            e.stopPropagation();
                            logoInputRef.current?.click();
                          }}
                        >
                          Upload Logo
                        </Button>
                      </>
                    )}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoSelect}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Recommended: Square image, at least 200x200px, PNG or JPG format
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="glass-card p-4 sm:p-6 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-foreground">Content</h3>
                </div>
                
                <div className="space-y-2">
                  <Label>Welcome Message</Label>
                  <Input
                    value={settings.welcomeMessage}
                    onChange={(e) => setSettings({ ...settings, welcomeMessage: e.target.value })}
                    placeholder="Book your appointment online"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Cancellation Policy</Label>
                  <Textarea
                    value={settings.cancellationPolicy}
                    onChange={(e) => setSettings({ ...settings, cancellationPolicy: e.target.value })}
                    placeholder="Your cancellation policy..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Terms and Conditions</Label>
                  <Textarea
                    value={settings.termsAndConditions}
                    onChange={(e) => setSettings({ ...settings, termsAndConditions: e.target.value })}
                    placeholder="Your terms and conditions..."
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    Customers must agree to these terms before booking
                  </p>
                </div>
              </div>

              <Button className="w-full" onClick={handleSaveChanges} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>

            {/* Live Preview - Hidden on mobile, shown on lg screens */}
            <div className="hidden lg:block glass-card p-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="text-xs text-muted-foreground">Live Preview</span>
              </div>
              <IPhonePreview themeStyle={previewThemeStyle} />
            </div>
          </div>
        </TabsContent>

        {/* Share Tab */}
        <TabsContent value="share" className="space-y-4 sm:space-y-6">
          <div className="glass-card p-4 sm:p-6 space-y-4 sm:space-y-6">
            <div>
              <h3 className="font-semibold text-foreground mb-2">Share Your Booking Page</h3>
              <p className="text-sm text-muted-foreground">
                Share this link with your customers so they can book appointments online
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Input value={bookingUrl} readOnly className="flex-1" />
              <Button onClick={handleCopyLink} className="w-full sm:w-auto">
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? "Copied" : "Copy Link"}
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 pt-4 border-t border-border">
              {/* QR Code */}
              <div className="rounded-xl border border-border bg-background/60 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <QrCode className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-semibold text-foreground">QR Code</p>
                </div>
                <div className="flex items-center justify-center">
                  <img
                    src={qrUrl}
                    alt="Booking page QR code"
                    className="h-[160px] w-[160px] rounded-lg border border-border bg-white p-2"
                    loading="lazy"
                  />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => copyText(bookingUrl, "Link copied")}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                  <Button variant="outline" onClick={downloadQr}>
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>

              {/* Embed */}
              <div className="rounded-xl border border-border bg-background/60 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Code className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-semibold text-foreground">Embed Widget</p>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Paste this on your website to embed your booking page.
                </p>
                <div className="rounded-lg border border-border bg-muted/40 p-3">
                  <code className="text-[11px] leading-4 text-foreground break-all">
                    {`<iframe src="${bookingUrl}" style="width:100%;min-height:720px;border:0;border-radius:12px;" loading="lazy"></iframe>`}
                  </code>
                </div>
                <div className="mt-3">
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() =>
                      copyText(
                        `<iframe src="${bookingUrl}" style="width:100%;min-height:720px;border:0;border-radius:12px;" loading="lazy"></iframe>`,
                        "Embed code copied",
                      )
                    }
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy embed code
                  </Button>
                </div>
              </div>

              {/* Social share */}
              <div className="rounded-xl border border-border bg-background/60 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Share2 className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-semibold text-foreground">Social Share</p>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Share your booking link to customers instantly.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    variant="outline"
                    onClick={() =>
                      openShare(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`)
                    }
                    title="Share on WhatsApp"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      openShare(
                        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(bookingUrl)}`,
                      )
                    }
                    title="Share on Facebook"
                  >
                    <Facebook className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      openShare(
                        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`,
                      )
                    }
                    title="Share on X"
                  >
                    <Twitter className="h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-3">
                  <Button className="w-full" variant="outline" onClick={() => copyText(shareMessage, "Share text copied")}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy share text
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* iPhone Preview in Share tab */}
          <div className="glass-card p-4 sm:p-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-xs text-muted-foreground">How customers will see it</span>
            </div>
            <IPhonePreview themeStyle={previewThemeStyle} />
          </div>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
